// Query hook for the My Day "Requests need action" section (#1600).
//
// Composes two read paths, each gated on `requests.read` so a `tasks.read`
// -only actor fires NO request API call at all (FR-4 / AC-5):
//   1. a candidate-request query that PAGES `GET /requests` to completion
//      inside the queryFn (loop until `nextCursor === null`, AC-4 — no
//      silent cap, no `useEffect`), and
//   2. the existing chunked chat-unread summary (AC-4) over the loaded ids.
// The pure `selectMyDayRequestSignals` selector turns both into ordered,
// deduped, non-PII rows. Errors are surfaced independently from the
// lead-task My Day (FR-6 / AC-6): this hook owns its own error/retry.
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type { RequestListQuery, RequestListRow } from '@magimp/shared';
import { listRequests } from '../../../api/requests/index.js';
import { hasPermission, useAuthStore } from '../../../stores/auth.js';
import { useRequestChatUnreadSummaries } from '../../requests/request-unread/index.js';
import {
  MY_DAY_CANDIDATE_STATUSES,
  selectMyDayRequestSignals,
  type MyDayRequestSignalRow,
} from './requestSignals.js';

// WHY (#1600 AC-4 / FR-5): page size is the issue's `limit=100`. The query
//   key lives under the `['requests', ...]` family with a stable filters
//   object so #1593's narrowed invalidation predicate (`key[0]==='requests'`
//   + `requestListDataHasRow` over `{ items }`) refreshes this section on a
//   single-request change WITHOUT broad list flashing.
const CANDIDATE_PAGE_SIZE = 100;

const CANDIDATE_FILTERS: Partial<RequestListQuery> = {
  status: [...MY_DAY_CANDIDATE_STATUSES],
  limit: CANDIDATE_PAGE_SIZE,
};

function myDayCandidatesKey() {
  return ['requests', 'my-day-candidates', CANDIDATE_FILTERS] as const;
}

interface CandidateData {
  // WHY (#1662): list rows omit chat/audit — `RequestListRow` is the
  //   accurate candidate shape. My-Day reads only id/status/sla/carriers
  //   (never chat/audit), so the narrower row is sufficient.
  items: RequestListRow[];
}

// Pages `GET /requests` to completion. Following `nextCursor` until it is
// `null` (AC-4) — every candidate is considered, not just the first page.
async function fetchAllCandidates(signal: AbortSignal): Promise<CandidateData> {
  const items: RequestListRow[] = [];
  let cursor: string | undefined;
  do {
    const page = await listRequests({ ...CANDIDATE_FILTERS, cursor }, { signal });
    items.push(...page.items);
    cursor = page.nextCursor ?? undefined;
  } while (cursor !== undefined);
  return { items };
}

export interface MyDayRequestSignalsResult {
  rows: readonly MyDayRequestSignalRow[];
  /**
   * Open a candidate request by id. My Day NAVIGATES to the China requests
   * workspace (`/china-rates?openId=<id>`) and lets `RequestsListPage` own the
   * open — no wire row is resolved or returned here, so the non-PII boundary
   * (#1600 AC-9) holds trivially.
   */
  openRequest: (id: string) => void;
  /** True only when the actor lacks `requests.read` (no API call is made). */
  disabled: boolean;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

export function useMyDayRequestSignals(): MyDayRequestSignalsResult {
  const perms = useAuthStore((s) => s.permissions);
  const canRead = hasPermission(perms, 'requests.read');

  const candidatesQuery = useQuery<CandidateData>({
    queryKey: myDayCandidatesKey(),
    queryFn: ({ signal }) => fetchAllCandidates(signal),
    enabled: canRead,
  });

  const candidateIds = useMemo<readonly string[]>(
    () => (candidatesQuery.data?.items ?? []).map((r) => r.id),
    [candidatesQuery.data],
  );

  // INVARIANT (#1600 AC-4): chunked summary handles >200 ids on its own;
  //   gated on `requests.read` AND on candidates being loaded so a no-read
  //   actor never hits the endpoint.
  const unread = useRequestChatUnreadSummaries(candidateIds, {
    enabled: canRead && candidateIds.length > 0,
  });

  const rows = useMemo<readonly MyDayRequestSignalRow[]>(
    () => selectMyDayRequestSignals(candidatesQuery.data?.items ?? [], unread.data),
    [candidatesQuery.data, unread.data],
  );

  const navigate = useNavigate();
  const openRequest = useCallback(
    (id: string) => {
      // WHY (#1753): My Day is not the requests workspace and mounts no
      //   request drawer, so opening must navigate to /china-rates and let
      //   RequestsListPage own the open (editable RETURN/DRAFT editor, drawer,
      //   URL state, dirty-close). Keeps CRM free of any requests-feature import.
      navigate(`/china-rates?openId=${encodeURIComponent(id)}`);
    },
    [navigate],
  );

  return {
    rows,
    openRequest,
    disabled: !canRead,
    isLoading: canRead && (candidatesQuery.isPending || unread.isLoading),
    isError: candidatesQuery.isError || unread.isError,
    refetch: () => {
      void candidatesQuery.refetch();
      unread.refetch();
    },
  };
}
